import { Icon } from '@livable/saas-react-components';
import { Chip, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatExtractedMoney } from '../BillAutomationExtraction';
import { APPFOLIO_PUSH_REFUSAL_CODES, APPFOLIO_PUSH_STATUS, GL_NOT_CONFIGURED, getAppfolioPushBlockerMessage, getAppfolioPushFailureMessage, getAppfolioPushRefusalMessage } from './appfolioPushConstants';
import useAppfolioPushStyles from './appfolioPushStyles';
import { PropertyDetailsLink } from './PropertyDetailsLink';

/** Where the push itself stands. Blockers are rendered on their own, so `failed` defers to them. */
const renderStatusChip = _ref => {
  var _liveAttempt$blockers;
  let liveAttempt = _ref.liveAttempt,
    pushStatus = _ref.pushStatus,
    t = _ref.t;
  switch (pushStatus === null || pushStatus === void 0 ? void 0 : pushStatus.status) {
    case APPFOLIO_PUSH_STATUS.succeeded:
      {
        // The figure AppFolio confirmed, never the one that was requested.
        const pushedAmount = formatExtractedMoney(pushStatus === null || pushStatus === void 0 ? void 0 : pushStatus.pushedAmount);
        const pushedOn = pushStatus !== null && pushStatus !== void 0 && pushStatus.pushedOn ? moment(pushStatus.pushedOn).format('MM/DD/YYYY') : '';
        const label = pushedOn ? "".concat(t('AP pushed on'), " ").concat(pushedOn, " \xB7 ").concat(pushedAmount) : "".concat(t('AP pushed'), " \xB7 ").concat(pushedAmount);
        return /*#__PURE__*/React.createElement(Chip, {
          icon: /*#__PURE__*/React.createElement(Icon, {
            type: "check"
          }),
          label: label,
          variant: "success"
        });
      }
    case APPFOLIO_PUSH_STATUS.pending:
      return /*#__PURE__*/React.createElement(Chip, {
        icon: /*#__PURE__*/React.createElement(Icon, {
          type: "info"
        }),
        label: t('Push requested — the status will update here shortly.'),
        variant: "info"
      });
    case APPFOLIO_PUSH_STATUS.needsReconciliation:
      return /*#__PURE__*/React.createElement(Chip, {
        icon: /*#__PURE__*/React.createElement(Icon, {
          type: "info"
        }),
        label: t('This invoice may hold two payables in AppFolio. Contact support to reconcile it.'),
        variant: "warning"
      });
    case APPFOLIO_PUSH_STATUS.failed:
      if (liveAttempt !== null && liveAttempt !== void 0 && (_liveAttempt$blockers = liveAttempt.blockers) !== null && _liveAttempt$blockers !== void 0 && _liveAttempt$blockers.length) return null;
      return /*#__PURE__*/React.createElement(Chip, {
        icon: /*#__PURE__*/React.createElement(Icon, {
          type: "info"
        }),
        label: t(getAppfolioPushFailureMessage(liveAttempt === null || liveAttempt === void 0 ? void 0 : liveAttempt.errorCode)),
        variant: "critical"
      });
    default:
      return null;
  }
};

/**
 * Everything the AP push has to say about one document: what the last trigger refused, what still blocks
 * it, and where the push stands. Rendered next to the amounts rather than in the modal footer.
 */
export const AppfolioPushMessages = props => {
  const classes = useAppfolioPushStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  // `className` is how a caller that stands these beside a control rather than under one drops the
  // top margin this carries for its own placement.
  const _props$blockers = props.blockers,
    blockers = _props$blockers === void 0 ? [] : _props$blockers,
    _props$className = props.className,
    className = _props$className === void 0 ? '' : _props$className,
    disabledReason = props.disabledReason,
    liveAttempt = props.liveAttempt,
    pushStatus = props.pushStatus,
    refusalCode = props.refusalCode;

  // The date a status read carries says more than the refusal itself, so it wins when both are known.
  const showRefusal = !!refusalCode && !(refusalCode === APPFOLIO_PUSH_REFUSAL_CODES.billAlreadyPushed && (pushStatus === null || pushStatus === void 0 ? void 0 : pushStatus.status) === APPFOLIO_PUSH_STATUS.succeeded);
  const statusChip = pushStatus !== null && pushStatus !== void 0 && pushStatus.duplicateOfBillId ? /*#__PURE__*/React.createElement(Chip, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "info"
    }),
    label: t('This invoice was already pushed to AppFolio from another bill document.'),
    variant: "warning"
  }) : renderStatusChip({
    liveAttempt,
    pushStatus,
    t
  });
  if (!showRefusal && !blockers.length && !statusChip && !disabledReason) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.messages, " ").concat(className)
  }, showRefusal ? /*#__PURE__*/React.createElement(Chip, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "info"
    }),
    label: t(getAppfolioPushRefusalMessage(refusalCode)),
    variant: "critical"
  }) : null, blockers.length ? /*#__PURE__*/React.createElement("div", {
    className: classes.blockerList
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.blockerTitle,
    variant: "body2"
  }, t('This bill cannot be pushed to AppFolio yet:')), blockers.map((blocker, index) => {
    var _blocker$propertyLiva;
    return /*#__PURE__*/React.createElement("div", {
      key: "".concat(blocker.code, "-").concat((_blocker$propertyLiva = blocker.propertyLivableId) !== null && _blocker$propertyLiva !== void 0 ? _blocker$propertyLiva : 'document', "-").concat(index)
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, t(getAppfolioPushBlockerMessage(blocker.code)), blocker.code === GL_NOT_CONFIGURED && blocker.propertyLivableId ? /*#__PURE__*/React.createElement(React.Fragment, null, ' ', /*#__PURE__*/React.createElement(PropertyDetailsLink, {
      className: classes.blockerLink,
      propertyLivableId: blocker.propertyLivableId
    }, t('Set it up'))) : null), blocker.detail ? /*#__PURE__*/React.createElement(Typography, {
      className: classes.blockerDetail,
      color: "light",
      variant: "body2"
    }, blocker.detail) : null);
  })) : null, statusChip, disabledReason && !statusChip ? /*#__PURE__*/React.createElement(Chip, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "info"
    }),
    label: t(disabledReason),
    variant: "info"
  }) : null);
};